import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  Linkedin,
  Instagram,
  Mail,
  Phone,
  MapPin,
  Globe,
  MessageSquare,
  ArrowRight,
  Newspaper
} from 'lucide-react';
import { SITE_VERSION } from '@/lib/version';
import { getVercelImageUrl } from '@/lib/images';
import { getPressReleases, PressRelease } from '@/data/pressReleases';
import { motion } from 'motion/react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getCMSPressReleases } from '@/lib/cms';
import { fetchLatestPressRelease } from '@/lib/supabase/public-content';
import { getSiteContacts, siteContactsDefaults, SiteContacts } from '@/lib/supabase/admin-api';
import { formatPressReleaseDate } from '@/lib/utils';

export default function Footer() {
  const { t, language } = useLanguage();

  // Merge static and CMS press releases (fallback enquanto o Supabase nao responde)
  const staticReleases = getPressReleases(language);
  const cmsReleases = getCMSPressReleases(language as 'pt' | 'es').map(c => ({
    id: c.slug,
    title: c.title,
    date: c.release_date,
    excerpt: c.excerpt,
    category: 'Release',
    content: c.body,
    author: 'TechEnabler'
  }));

  const fallbackReleases = [...cmsReleases, ...staticReleases];
  const currentYear = new Date().getFullYear();

  // Requisito 3: usa o publicado mais recente de fato (nao o destaque).
  const [lastPressRelease, setLastPressRelease] = useState<PressRelease | null>(fallbackReleases[0] || null);

  useEffect(() => {
    let active = true;
    fetchLatestPressRelease(language).then((item) => {
      if (active && item) setLastPressRelease(item);
    });
    return () => {
      active = false;
    };
  }, [language]);

  // Requisito 12: contatos editaveis via admin, com fallback para os valores estaticos atuais.
  const [contacts, setContacts] = useState<SiteContacts>(siteContactsDefaults);

  useEffect(() => {
    let active = true;
    getSiteContacts()
      .then((data) => {
        if (active) setContacts(data);
      })
      .catch(() => {
        // mantem os defaults estaticos em caso de erro (padrao de resiliencia do projeto)
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <footer className="dark bg-[#0b0d10] text-[#f5f5f5] border-t border-white/5 pt-24">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 mb-20">
          {/* Brand and Description */}
          <div className="lg:col-span-3 space-y-10">
            <Link to="/" className="-m-1.5 p-1.5 flex items-center gap-2 group">
              <img
                src="/techenabler-logo-branco.webp"
                alt="TechEnabler"
                width={479}
                height={120}
                className="h-10 w-auto group-hover:scale-105 transition-transform translate-y-[-2px] object-contain"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed max-w-sm">
              {t('Especialista em integração e operação de soluções para infraestruturas críticas. Proteção, observabilidade e desempenho de redes.', 'Especialista en integración y operación de soluciones para infraestructuras críticas. Protección, observabilidad y rendimiento de redes.', 'Specialist in integrating and operating solutions for critical infrastructures. Network protection, observability and performance.')}
            </p>
            
            {/* System Status - Cloud Vibe */}
            <div className="pt-4 border-t border-white/5">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
                  <div className="absolute inset-0 w-2 h-2 rounded-full bg-emerald-500 animate-ping opacity-40" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-500/80">{t('Operação Global Normalizada', 'Operación Global Normalizada', 'Global Operations Normal')}</span>
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <a
                href={contacts.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:border-primary hover:bg-primary/10 hover:text-primary transition-all text-gray-400"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href={contacts.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:border-primary hover:bg-primary/10 hover:text-primary transition-all text-gray-400"
              >
                <Instagram className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Links Sections */}
          <div className="lg:col-span-6 grid grid-cols-2 lg:grid-cols-3 gap-12">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-8 underline decoration-primary/30 underline-offset-8">{t('Serviços', 'Servicios', 'Services')}</h3>
              <ul className="space-y-4 text-sm text-gray-400">
                <li><Link to="/servicos/local-scrubbing-as-a-service-lsaas" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />DDoS Local</Link></li>
                <li><Link to="/servicos/cloud-defense" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />DDoS Cloud</Link></li>
                <li><Link to="/servicos/smart-dns-isps" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />Smart DNS</Link></li>
                <li><Link to="/servicos/network-edge-core-observability-providers" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />Observabilidade</Link></li>
                <li><Link to="/solucoes" className="text-primary font-medium hover:underline flex items-center gap-2 mt-4 italic text-xs">{t('Catálogo', 'Catálogo', 'Catalog')} <ArrowRight className="h-3 w-3" /></Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-8 underline decoration-primary/30 underline-offset-8">{t('Mercados', 'Mercados', 'Markets')}</h3>
              <ul className="space-y-4 text-sm text-gray-400">
                <li><Link to="/mercados/operadoras-isps" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />Operadoras/ISPs</Link></li>
                <li><Link to="/mercados/operadores-servicos-nuvem" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />{t('Cloud/SaaS', 'Cloud/SaaS')}</Link></li>
                <li><Link to="/mercados/redes-corporativas" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />{t('Enterprise', 'Enterprise')}</Link></li>
                <li><Link to="/mercados/ti-corporativa" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />{t('TI Corporativa', 'TI Corporativa')}</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-8 underline decoration-primary/30 underline-offset-8">{t('Empresa', 'Empresa', 'Company')}</h3>
              <ul className="space-y-4 text-sm text-gray-400">
                <li><Link to="/sobre-nos" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />{t('Sobre nós', 'Sobre nosotros', 'About us')}</Link></li>
                <li><Link to="/parceiros" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />{t('Parceiros', 'Socios', 'Partners')}</Link></li>
                <li><Link to="/sala-de-imprensa" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />{t('Imprensa e Comunicação', 'Prensa y Comunicación', 'Press and Communications')}</Link></li>
                <li><Link to="/contato" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />{t('Fale conosco', 'Hable con nosotros', 'Contact us')}</Link></li>
                <li><Link to="/solucoes/inventario" className="hover:text-white transition-colors flex items-center gap-2 group"><span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />{t('Inventário', 'Inventario', 'Inventory')}</Link></li>
              </ul>
            </div>
          </div>

          {/* Latest News Card */}
          <div className="lg:col-span-3">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-8">{t('Último Press Release', 'Último Comunicado de Prensa', 'Latest Press Release')}</h3>
            {lastPressRelease && (
              <motion.div 
                whileHover={{ y: -5 }}
                className="group relative bg-[#111317] rounded-2xl border border-white/5 overflow-hidden shadow-2xl"
              >
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2 text-primary font-bold tracking-widest text-[9px] uppercase">
                    <Newspaper className="h-3 w-3" />
                    <span>{formatPressReleaseDate(lastPressRelease.date, language)}</span>
                  </div>
                  <h4 className="text-sm font-semibold leading-tight group-hover:text-primary transition-colors line-clamp-3">
                    {lastPressRelease.title}
                  </h4>
                  <Link 
                    to={`/sala-de-imprensa/press-releases/${lastPressRelease.id}`}
                    className="pt-4 flex items-center gap-2 text-gray-400 hover:text-white text-[10px] font-bold uppercase tracking-widest transition-colors"
                  >
                    {t('Ler Completo', 'Leer Completo', 'Read in Full')} <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.div>
            )}
          </div>
        </div>

        {/* Contact Info Bar */}
        <div className="py-12 border-t border-white/5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="flex gap-5">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
              <MapPin className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50 mb-2">{t('Escritório Central', 'Oficina Central', 'Head Office')}</h4>
              <p className="text-xs text-gray-400 leading-relaxed">
                {contacts.address.line1}<br />
                {contacts.address.line2}
              </p>
            </div>
          </div>
          <div className="flex gap-5">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
              <Phone className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50 mb-2">{t('Telefone', 'Teléfono', 'Phone')}</h4>
              <p className="text-xs text-gray-400 leading-relaxed font-mono">
                {contacts.phones.map((phone) => (
                  <span key={phone}>{phone}<br /></span>
                ))}
              </p>
            </div>
          </div>
          <div className="flex gap-5">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50 mb-2">E-mail</h4>
              <p className="text-xs text-gray-400 leading-relaxed underline decoration-white/10 decoration-1 underline-offset-4">
                {contacts.emails.contact}<br />
                {contacts.emails.commercial}
              </p>
            </div>
          </div>
          <div className="flex gap-5">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
              <MessageSquare className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50 mb-2">{t('Imprensa e Comunicação', 'Prensa y Comunicación', 'Press and Communications')}</h4>
              <p className="text-xs text-gray-400 leading-relaxed italic">
                {contacts.press_contact.name}<br />
                {contacts.press_contact.email}<br />
                {contacts.press_contact.phone}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-black/40 py-10 border-t border-white/5">
        <div className="container-custom flex flex-col md:flex-row justify-between items-center gap-8 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-center md:text-left">
            <p className="text-white/60 italic tracking-normal font-sans text-[11px]">© {currentYear} TechEnabler. {t('Todos os direitos reservados.', 'Todos los derechos reservados.', 'All rights reserved.')}</p>
            <p className="text-white/20 hidden md:block select-none">•</p>
            <a
              href="https://nosailab.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/55 tracking-normal font-sans text-[11px] hover:text-white transition-colors"
            >
              {t('Desenvolvido por nós, ', 'Desarrollado por nosotros, ', 'Developed by us, ')}<span className="italic text-primary">{t('Humanos e IA.', 'Humanos e IA.', 'Humans and AI.')}</span>
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-10 items-center">
            <Link to="/privacidade" className="hover:text-white transition-colors">{t('Privacidade', 'Privacidad', 'Privacy')}</Link>
            <Link to="/termos-de-uso" className="hover:text-white transition-colors">{t('Termos de Uso', 'Términos de Uso', 'Terms of Use')}</Link>
            <span className="text-white/55 tracking-normal font-sans text-[11px] select-none">{SITE_VERSION}</span>
            <div className="flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full border border-white/10 text-white/60">
              <Globe className="h-3 w-3" />
              <span>{language === 'es' ? 'ES' : language === 'en' ? 'EN' : 'PT-BR'}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

